import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { HomeShell } from "@/shell/HomeShell";
import { candidateApi } from "@/lib/candidateApi";
import { initialsFromName } from "@/lib/initials";
import type { KbProfileData } from "@/lib/types";

// ── Drill definitions ──────────────────────────────────────────────────────
const DRILLS = [
  {
    id: "tmay",
    title: "Tell Me About Yourself",
    abbr: "TMAY",
    hint: "A 90-second structured narrative: origin → journey → now → why here.",
    url: "https://www.youtube.com/watch?v=TQHW7gGjrCQ",
  },
  {
    id: "pcar",
    title: "The P-CAR Framework",
    abbr: "PCAR",
    hint: "Problem → Constraint → Action → Result. The backbone of every behavioural answer.",
    url: "https://www.youtube.com/watch?v=dRqN4BuhCHU",
  },
  {
    id: "outro",
    title: "The Outro",
    abbr: "Outro",
    hint: "Closing strong: a forward-looking statement that ties your narrative to this role.",
    url: "https://www.youtube.com/watch?v=Vy5wxhFBXEM",
  },
] as const;

// ── Sub-components ──────────────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-[13px] italic py-2" style={{ color: "#8C86A0" }}>
      {message}
    </p>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-[18px] border p-5 flex flex-col gap-3"
      style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
    >
      <p
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
        style={{ color: "#7C7590" }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────
type Tab = "about" | "company" | "drills";

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: "about", label: "About Me" },
    { id: "company", label: "Company" },
    { id: "drills", label: "Drills" },
  ];
  return (
    <div
      className="flex gap-1 rounded-xl p-1 w-full md:w-auto"
      style={{ background: "#F3F1FA" }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className="flex-1 md:flex-none px-4 py-1.5 rounded-lg text-[13px] font-semibold transition-colors"
          style={{
            background: active === t.id ? "#FFFFFF" : "transparent",
            color: active === t.id ? "#17131F" : "#7C7590",
            boxShadow: active === t.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Tab panels ──────────────────────────────────────────────────────────────

function AboutMePanel({ data }: { data: KbProfileData["aboutMe"] }) {
  return (
    <div className="flex flex-col gap-4">
      <Section label="Career Narrative">
        {data.careerNarrative ? (
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "#4A4260" }}>
            {data.careerNarrative}
          </p>
        ) : (
          <EmptyState message="No career narrative yet. Complete a session to generate one." />
        )}
      </Section>

      <Section label="Strengths">
        {data.strengthsSummary ? (
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "#4A4260" }}>
            {data.strengthsSummary}
          </p>
        ) : (
          <EmptyState message="Strengths summary will appear here after your first scored session." />
        )}
      </Section>

      <Section label="Narrative Bank">
        {data.narrativeBank.length === 0 ? (
          <EmptyState message="Your best answers will be saved here as you practise." />
        ) : (
          <div className="flex flex-col gap-3">
            {data.narrativeBank.map((entry, i) => (
              <div
                key={i}
                className="rounded-lg p-3.5"
                style={{ background: "#F3F1FA", borderLeft: "3px solid #7A6BE0", paddingLeft: 14 }}
              >
                <p
                  className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] mb-1"
                  style={{ color: "#7C7590" }}
                >
                  {entry.drill} · {entry.session_date}
                </p>
                <p className="text-[13.5px] leading-relaxed" style={{ color: "#4A4260" }}>
                  {entry.answer}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function CompanyPanel({ data }: { data: KbProfileData["company"] }) {
  return (
    <div className="flex flex-col gap-4">
      <Section label="Company">
        <div>
          <p className="text-[18px] font-semibold" style={{ color: "#17131F" }}>
            {data.name || "—"}
          </p>
          {data.role && (
            <p className="text-[13px] mt-0.5 font-mono" style={{ color: "#7C7590" }}>
              {data.role}
            </p>
          )}
        </div>
      </Section>

      <Section label="Research Brief">
        {data.research ? (
          <div className="prose-scorecard" dangerouslySetInnerHTML={{ __html: data.researchHtml }} />
        ) : (
          <EmptyState message="No research brief available yet. Your consultant can generate one from the recruiter portal." />
        )}
      </Section>
    </div>
  );
}

function DrillsPanel() {
  return (
    <div className="flex flex-col gap-4">
      {DRILLS.map((drill) => (
        <div
          key={drill.id}
          className="rounded-[18px] border p-5 flex flex-col gap-3"
          style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p
                className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] mb-1"
                style={{ color: "var(--accent)" }}
              >
                {drill.abbr}
              </p>
              <p className="text-[15px] font-semibold" style={{ color: "#17131F" }}>
                {drill.title}
              </p>
            </div>
            <a
              href={drill.url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-semibold transition-colors"
              style={{ background: "var(--accent-50)", color: "#4A4260" }}
            >
              {/* ponytail: inline SVG, no icon lib */}
              <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M10 16.5l6-4.5-6-4.5v9zm2-14.5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
              </svg>
              Watch
            </a>
          </div>
          <p className="text-[13.5px] leading-relaxed" style={{ color: "#7C7590" }}>
            {drill.hint}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function KB() {
  const { token = "" } = useParams<{ token: string }>();
  const [activeTab, setActiveTab] = useState<Tab>("about");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<KbProfileData | null>(null);

  // Branding for shell
  const [agencyName, setAgencyName] = useState<string | undefined>(undefined);
  const [candidateName, setCandidateName] = useState<string | undefined>(undefined);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [logoFilter, setLogoFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    Promise.all([
      candidateApi.getInvite(token),
      candidateApi.getKbProfile(token),
    ]).then(([inv, kb]) => {
      setLoading(false);
      if (inv.ok) {
        setAgencyName(inv.agencyName);
        setCandidateName(inv.candidateName);
        setLogoUrl(inv.branding.logoUrl || undefined);
        setLogoFilter(inv.branding.logoFilter || undefined);
      }
      if (!kb.ok) {
        setError(kb.error ?? "Could not load knowledge base.");
        return;
      }
      setData(kb);
    });
  }, [token]);

  return (
    <HomeShell
      agencyName={agencyName}
      candidateInitials={initialsFromName(candidateName)}
      logoUrl={logoUrl}
      logoFilter={logoFilter}
      activeTab="kb"
    >
      <div className="max-w-[680px]">
        <div className="mb-5">
          <p
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "#7C7590" }}
          >
            Preparation
          </p>
          <h1 className="text-[22px] font-semibold mt-1" style={{ color: "#17131F" }}>
            Knowledge Base
          </h1>
        </div>

        {/* Tab bar */}
        <div className="mb-5">
          <TabBar active={activeTab} onChange={setActiveTab} />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center gap-2" style={{ color: "#7C7590" }}>
            <div
              className="w-4 h-4 rounded-full border-2 animate-spin"
              style={{ borderColor: "#B4AEC4", borderTopColor: "var(--accent)" }}
            />
            <span className="text-[13px]">Loading…</span>
          </div>
        ) : error ? (
          <p className="text-[13px]" style={{ color: "#E1573F" }}>
            {error}
          </p>
        ) : (
          <>
            {activeTab === "about" && data && <AboutMePanel data={data.aboutMe} />}
            {activeTab === "company" && data && <CompanyPanel data={data.company} />}
            {activeTab === "drills" && <DrillsPanel />}
          </>
        )}
      </div>
    </HomeShell>
  );
}
